// services/google.service.js
import connectionsClient, { handleApiError } from '../api/connectionsClient';

/**
 * Servicio para gestionar integraciones de Google (Gmail, Drive, Calendar)
 * Base URL: https://conn-dev.zenitailab.com
 * 
 * Endpoints totales: 24
 * - OAuth: 5 endpoints
 * - Gmail: 6 endpoints
 * - Drive: 8 endpoints
 * - Calendar: 5 endpoints
 */
class GoogleService {
  // ============================================================================
  // 🔐 GOOGLE OAUTH (/google/oauth) - 5 endpoints
  // ============================================================================

  /**
   * Generar URL de autorización OAuth
   * POST /google/oauth/authorize
   * 
   * @param {Object} params
   * @param {string} params.serviceType - "calendar" | "drive" | "gmail"
   * @param {string} [params.redirectUri] - URI de redirección personalizada
   * @param {string} [params.state] - Estado para verificación CSRF
   * @param {string[]} [params.scopes] - Scopes adicionales
   * @param {string} [params.accessType] - "offline" por defecto
   * @param {string} [params.prompt] - "consent" | "select_account"
   * @param {string} [params.returnUrl] - URL a donde volver después del OAuth
   * @returns {Promise<{success: boolean, data?: {authUrl: string}, error?: any}>}
   */
  async authorize({ serviceType, redirectUri, state, scopes, accessType = 'offline', prompt, userId, accountId, returnUrl }) {
    try {
      if (!serviceType) {
        return {
          success: false,
          error: { message: 'service_type is required' }
        };
      }

      const payload = {
        service_type: serviceType,
        redirect_uri: redirectUri,
        state,
        scopes,
        access_type: accessType,
        prompt,
        user_id: userId,
        account_id: accountId,
        return_url: returnUrl  // Backend lo codificará en base64 y lo incluirá en el state
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/google/oauth/authorize', payload);
      
      // Manejar respuesta anidada del backend (statusCode, headers, body)
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      // Extraer datos de la respuesta
      const responseData = data.data || data;

      return {
        success: true,
        data: {
          authUrl: responseData.authorization_url,
          state: responseData.state,
          requestId: responseData.request_id,
          serviceType: responseData.service_type,
          scopes: responseData.requested_scopes,
          redirectUri: responseData.redirect_uri
        }
      };
    } catch (error) {
      console.error('Error generating Google auth URL:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener estado de integración OAuth
   * GET /google/oauth/status/{service_type}?account_id=xxx
   * 
   * @param {string} serviceType - "calendar" | "drive" | "gmail"
   * @param {string} accountId - ID de la cuenta
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getAuthStatus(serviceType, accountId) {
    try {
      if (!serviceType) {
        return {
          success: false,
          error: { message: 'service_type is required' }
        };
      }

      if (!accountId) {
        return {
          success: false,
          error: { message: 'account_id is required' }
        };
      }

      const response = await connectionsClient.get(`/google/oauth/status/${serviceType}`, {
        params: { account_id: accountId }
      });
      
      // Manejar respuesta anidada del backend
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('Error getting Google auth status:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Refrescar token de acceso
   * POST /google/oauth/refresh
   * 
   * @param {string} serviceType - "calendar" | "drive" | "gmail"
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async refreshToken(serviceType) {
    try {
      const response = await connectionsClient.post('/google/oauth/refresh', {
        service_type: serviceType
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error refreshing Google token:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Procesar callback de OAuth (intercambiar código por tokens)
   * GET /google/oauth/callback
   * 
   * @param {Object} params
   * @param {string} params.code - Código de autorización de Google
   * @param {string} params.state - Estado para verificación CSRF
   * @param {string} params.redirectUri - URI de redirección usada
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async processCallback({ code, state, redirectUri }) {
    try {
      if (!code || !state) {
        return {
          success: false,
          error: { message: 'code and state are required' }
        };
      }

      const response = await connectionsClient.get('/google/oauth/callback', {
        params: {
          code,
          state,
          redirect_uri: redirectUri
        }
      });
      
      // Manejar respuesta anidada del backend
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('Error processing Google OAuth callback:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Revocar tokens y desconectar
   * POST /google/oauth/revoke
   *
   * @param {string} serviceType - "calendar" | "drive" | "gmail"
   * @param {string} [integrationId] - opcional; fila específica a revocar.
   *   Si se omite, el backend resuelve por (user_id, service_type) — esto
   *   puede deactivar una fila distinta a la pretendida cuando el usuario
   *   tiene varias conexiones activas del mismo tipo.
   * @param {string} [accountId] - opcional; account_id del integration.
   *   Cuando se pasa junto con integrationId, el backend valida ownership
   *   por pertenencia al account (cualquier usuario del account puede
   *   revocar integraciones org-level compartidas).
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async revokeAuth(serviceType, integrationId, accountId) {
    try {
      const body = { service_type: serviceType };
      if (integrationId) {
        body.integration_id = integrationId;
      }
      if (accountId) {
        body.account_id = accountId;
      }
      const response = await connectionsClient.post('/google/oauth/revoke', body);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error revoking Google auth:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  // ============================================================================
  // 📧 GMAIL (/gmail) - 6 endpoints
  // ============================================================================

  /**
   * Enviar email
   * POST /gmail/messages/send
   * 
   * @param {Object} params
   * @param {string[]} params.to - Destinatarios (REQUIRED)
   * @param {string} params.subject - Asunto (REQUIRED)
   * @param {string} [params.bodyText] - Cuerpo texto plano
   * @param {string} [params.bodyHtml] - Cuerpo HTML
   * @param {string[]} [params.cc] - Copia
   * @param {string[]} [params.bcc] - Copia oculta
   * @param {string} [params.replyTo] - Reply-To
   * @param {string} [params.threadId] - Para respuestas en hilo
   * @param {Array<{filename: string, content: string, mimeType: string}>} [params.attachments]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async sendEmail({ to, subject, bodyText, bodyHtml, cc, bcc, replyTo, threadId, attachments }) {
    try {
      if (!to || !to.length || !subject) {
        return {
          success: false,
          error: { message: 'to and subject are required' }
        };
      }

      const payload = {
        to,
        subject,
        body_text: bodyText,
        body_html: bodyHtml,
        cc,
        bcc,
        reply_to: replyTo,
        thread_id: threadId,
        attachments: attachments?.map(att => ({
          filename: att.filename,
          content: att.content,
          mime_type: att.mimeType
        }))
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/gmail/messages/send', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error sending email:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar mensajes de Gmail
   * GET /gmail/messages
   * 
   * @param {Object} [params]
   * @param {string} [params.query] - Gmail search query
   * @param {string} [params.labelIds] - Filtrar por labels
   * @param {number} [params.maxResults] - default: 10, max: 500
   * @param {string} [params.pageToken] - Token de paginación
   * @param {boolean} [params.includeSpamTrash]
   * @param {string} [params.formatType] - "minimal" | "full"
   * @param {string} [params.sortOrder] - "asc" | "desc"
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async listMessages({ query, labelIds, maxResults = 10, pageToken, includeSpamTrash, formatType, sortOrder } = {}) {
    try {
      const params = {
        query,
        label_ids: labelIds,
        max_results: maxResults,
        page_token: pageToken,
        include_spam_trash: includeSpamTrash,
        format_type: formatType,
        sort_order: sortOrder
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get('/gmail/messages', { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error listing Gmail messages:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener mensaje específico
   * GET /gmail/messages/{message_id}
   * 
   * @param {string} messageId - ID del mensaje (REQUIRED)
   * @param {Object} [options]
   * @param {string} [options.formatType] - "full" | "minimal"
   * @param {boolean} [options.includeAttachments]
   * @param {boolean} [options.processHtml]
   * @param {boolean} [options.extractLinks]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getMessage(messageId, { formatType, includeAttachments, processHtml, extractLinks } = {}) {
    try {
      if (!messageId) {
        return {
          success: false,
          error: { message: 'message_id is required' }
        };
      }

      const params = {
        format_type: formatType,
        include_attachments: includeAttachments,
        process_html: processHtml,
        extract_links: extractLinks
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get(`/gmail/messages/${messageId}`, { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error getting Gmail message:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Modificar labels de mensajes (read/unread)
   * POST /gmail/messages/{message_id}/modify
   * 
   * @param {string} messageId - ID del mensaje (REQUIRED)
   * @param {Object} params
   * @param {string[]} params.messageIds - IDs de mensajes a modificar (REQUIRED)
   * @param {boolean} params.markAsRead - true = read, false = unread
   * @param {boolean} [params.verifyStatus]
   * @param {number} [params.batchSize] - default: 100
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async modifyMessage(messageId, { messageIds, markAsRead, verifyStatus, batchSize = 100 }) {
    try {
      if (!messageId || !messageIds || messageIds.length === 0) {
        return {
          success: false,
          error: { message: 'message_id and message_ids are required' }
        };
      }

      const payload = {
        message_ids: messageIds,
        mark_as_read: markAsRead,
        verify_status: verifyStatus,
        batch_size: batchSize
      };

      const response = await connectionsClient.post(`/gmail/messages/${messageId}/modify`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error modifying Gmail message:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Sincronizar mensajes de Gmail
   * POST /gmail/sync
   * 
   * @param {Object} [params]
   * @param {string} [params.syncType] - "full" | "incremental" | "partial"
   * @param {string} [params.lastHistoryId]
   * @param {string} [params.lastSyncTime]
   * @param {number} [params.maxResults]
   * @param {string[]} [params.labelIds]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async syncMessages({ syncType, lastHistoryId, lastSyncTime, maxResults, labelIds } = {}) {
    try {
      const payload = {
        sync_type: syncType,
        last_history_id: lastHistoryId,
        last_sync_time: lastSyncTime,
        max_results: maxResults,
        label_ids: labelIds
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/gmail/sync', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error syncing Gmail messages:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Leer emails con AI
   * POST /gmail/read
   * 
   * @param {Object} [params]
   * @param {string} [params.query] - Query de búsqueda
   * @param {number} [params.maxResults]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async readEmailsWithAI(params = {}) {
    try {
      const response = await connectionsClient.post('/gmail/read', params);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error reading emails with AI:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  // ============================================================================
  // 📁 GOOGLE DRIVE (/drive) - 8 endpoints
  // ============================================================================

  /**
   * Listar archivos de Drive
   * GET /drive/files
   * 
   * @param {Object} [params]
   * @param {string} [params.folderId] - ID de carpeta
   * @param {number} [params.pageSize] - default: 10
   * @param {string} [params.query] - Search query
   * @param {string[]} [params.fileTypes] - MIME types
   * @param {boolean} [params.includeShared]
   * @param {boolean} [params.includeTrashed]
   * @param {string} [params.orderBy] - default: "modifiedTime desc"
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async listFiles({ folderId, pageSize = 10, query, fileTypes, includeShared, includeTrashed, orderBy } = {}) {
    try {
      const params = {
        folder_id: folderId,
        page_size: pageSize,
        query,
        file_types: fileTypes?.join(','),
        include_shared: includeShared,
        include_trashed: includeTrashed,
        order_by: orderBy
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get('/drive/files', { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error listing Drive files:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Crear archivo en Drive
   * POST /drive/files
   * 
   * @param {Object} params
   * @param {string} params.name - Nombre del archivo (REQUIRED)
   * @param {string[]} [params.parents] - IDs de carpetas padre
   * @param {string} [params.mimeType]
   * @param {string} [params.description]
   * @param {boolean} [params.starred]
   * @param {string} [params.content] - Para archivos de texto
   * @param {Object} [params.properties] - Propiedades personalizadas
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async createFile({ name, parents, mimeType, description, starred, content, properties }) {
    try {
      if (!name) {
        return {
          success: false,
          error: { message: 'name is required' }
        };
      }

      const payload = {
        name,
        parents,
        mime_type: mimeType,
        description,
        starred,
        content,
        properties
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/drive/files', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Actualizar archivo en Drive
   * PUT /drive/files/{file_id}
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} params
   * @param {string} [params.name]
   * @param {string} [params.description]
   * @param {boolean} [params.starred]
   * @param {boolean} [params.trashed]
   * @param {string[]} [params.parents]
   * @param {string} [params.content]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async updateFile(fileId, { name, description, starred, trashed, parents, content }) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const payload = {
        file_id: fileId,
        name,
        description,
        starred,
        trashed,
        parents,
        content
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.put(`/drive/files/${fileId}`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar archivo de Drive
   * DELETE /drive/files/{file_id}
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} [options]
   * @param {boolean} [options.permanentDelete] - default: false (trash)
   * @param {boolean} [options.verifyDeletion]
   * @param {boolean} [options.backupMetadata]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async deleteFile(fileId, { permanentDelete = false, verifyDeletion, backupMetadata } = {}) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const response = await connectionsClient.delete(`/drive/files/${fileId}`, {
        data: {
          file_id: fileId,
          permanent_delete: permanentDelete,
          verify_deletion: verifyDeletion,
          backup_metadata: backupMetadata
        }
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error deleting Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Mover archivo en Drive
   * POST /drive/files/{file_id}/move
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} params
   * @param {string} [params.destinationFolderId] - Carpeta destino
   * @param {string[]} [params.removeFromParents] - Carpetas a remover
   * @param {string} [params.newName] - Nuevo nombre
   * @param {boolean} [params.verifyMove]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async moveFile(fileId, { destinationFolderId, removeFromParents, newName, verifyMove }) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const payload = {
        file_id: fileId,
        destination_folder_id: destinationFolderId,
        remove_from_parents: removeFromParents,
        new_name: newName,
        verify_move: verifyMove
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post(`/drive/files/${fileId}/move`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error moving Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Descargar archivo de Drive
   * GET /drive/files/{file_id}/download
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async downloadFile(fileId) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const response = await connectionsClient.get(`/drive/files/${fileId}/download`, {
        responseType: 'blob'
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error downloading Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Compartir archivo en Drive
   * POST /drive/files/{file_id}/share
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} params
   * @param {string} [params.emailAddress] - Usuario a compartir
   * @param {string} [params.role] - "reader" | "writer" | "owner"
   * @param {string} [params.type] - "user" | "group" | "domain" | "anyone"
   * @param {Array<{type: string, role: string, emailAddress?: string}>} [params.permissions]
   * @param {boolean} [params.sendNotificationEmail]
   * @param {string} [params.emailMessage]
   * @param {boolean} [params.makePublic]
   * @param {boolean} [params.generateShareableLink]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async shareFile(fileId, { emailAddress, role, type, permissions, sendNotificationEmail, emailMessage, makePublic, generateShareableLink }) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const payload = {
        file_id: fileId,
        email_address: emailAddress,
        role,
        type,
        permissions,
        send_notification_email: sendNotificationEmail,
        email_message: emailMessage,
        make_public: makePublic,
        generate_shareable_link: generateShareableLink
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post(`/drive/files/${fileId}/share`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error sharing Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Subir archivo a Drive
   * POST /drive/upload
   * 
   * @param {Object} params
   * @param {string} params.name - Nombre del archivo (REQUIRED)
   * @param {Blob|ArrayBuffer|string} params.fileContent - Contenido del archivo (REQUIRED)
   * @param {string[]} [params.parents] - IDs de carpetas padre
   * @param {string} [params.mimeType] - Auto-detectado si omitido
   * @param {string} [params.description]
   * @param {boolean} [params.makePublic]
   * @param {string[]} [params.shareWithEmails]
   * @param {boolean} [params.convertToGoogleFormat]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async uploadFile({ name, fileContent, parents, mimeType, description, makePublic, shareWithEmails, convertToGoogleFormat }) {
    try {
      if (!name || !fileContent) {
        return {
          success: false,
          error: { message: 'name and file_content are required' }
        };
      }

      // Crear FormData para upload multipart
      const formData = new FormData();
      formData.append('name', name);
      
      if (fileContent instanceof Blob) {
        formData.append('file_content', fileContent, name);
      } else {
        // Si es string o ArrayBuffer, convertir a Blob
        const blob = new Blob([fileContent], { type: mimeType || 'application/octet-stream' });
        formData.append('file_content', blob, name);
      }

      if (parents) formData.append('parents', JSON.stringify(parents));
      if (mimeType) formData.append('mime_type', mimeType);
      if (description) formData.append('description', description);
      if (makePublic !== undefined) formData.append('make_public', String(makePublic));
      if (shareWithEmails) formData.append('share_with_emails', JSON.stringify(shareWithEmails));
      if (convertToGoogleFormat !== undefined) formData.append('convert_to_google_format', String(convertToGoogleFormat));

      const response = await connectionsClient.post('/drive/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error uploading to Drive:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  // ============================================================================
  // 📅 GOOGLE CALENDAR (/calendar) - 5 endpoints
  // ============================================================================

  /**
   * Listar eventos del calendario
   * GET /calendar/events
   * 
   * @param {Object} [params]
   * @param {string} [params.calendarId] - default: "primary"
   * @param {string} [params.timeMin] - ISO datetime
   * @param {string} [params.timeMax] - ISO datetime
   * @param {number} [params.maxResults] - default: 250, max: 2500
   * @param {string} [params.pageToken]
   * @param {string} [params.query] - Free text search
   * @param {boolean} [params.singleEvents] - Expand recurring
   * @param {string} [params.orderBy] - "startTime" | "updated"
   * @param {boolean} [params.showDeleted]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async listEvents({ calendarId = 'primary', timeMin, timeMax, maxResults = 250, pageToken, query, singleEvents, orderBy, showDeleted } = {}) {
    try {
      const params = {
        calendar_id: calendarId,
        time_min: timeMin,
        time_max: timeMax,
        max_results: maxResults,
        page_token: pageToken,
        query,
        single_events: singleEvents,
        order_by: orderBy,
        show_deleted: showDeleted
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get('/calendar/events', { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error listing Calendar events:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Crear evento en el calendario
   * POST /calendar/events
   * 
   * @param {Object} params
   * @param {string} params.summary - Título del evento (REQUIRED)
   * @param {string} params.startDatetime - ISO format (REQUIRED)
   * @param {string} params.endDatetime - ISO format (REQUIRED)
   * @param {string} [params.description]
   * @param {string} [params.location]
   * @param {string[]} [params.attendeeEmails]
   * @param {string} [params.calendarId] - default: "primary"
   * @param {string[]} [params.recurrence] - RRULE strings
   * @param {Array<{method: string, minutes: number}>} [params.reminders]
   * @param {string} [params.visibility] - "default" | "public" | "private"
   * @param {string} [params.timezone] - default: "UTC"
   * @param {boolean} [params.allDay]
   * @param {boolean} [params.sendNotifications]
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async createEvent({ summary, startDatetime, endDatetime, description, location, attendeeEmails, calendarId = 'primary', recurrence, reminders, visibility, timezone = 'UTC', allDay, sendNotifications }) {
    try {
      if (!summary || !startDatetime || !endDatetime) {
        return {
          success: false,
          error: { message: 'summary, start_datetime, and end_datetime are required' }
        };
      }

      const payload = {
        summary,
        start_datetime: startDatetime,
        end_datetime: endDatetime,
        description,
        location,
        attendee_emails: attendeeEmails,
        calendar_id: calendarId,
        recurrence,
        reminders,
        visibility,
        timezone,
        all_day: allDay,
        send_notifications: sendNotifications
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/calendar/events', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating Calendar event:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Actualizar evento del calendario
   * PUT /calendar/events/{event_id}
   * 
   * @param {string} eventId - ID del evento (REQUIRED)
   * @param {Object} params
   * @param {string} [params.calendarId] - default: "primary"
   * @param {string} [params.summary]
   * @param {string} [params.description]
   * @param {string} [params.location]
   * @param {string} [params.startTime]
   * @param {string} [params.endTime]
   * @param {Array<{email: string, responseStatus?: string}>} [params.attendees]
   * @param {string[]} [params.recurrence]
   * @param {string} [params.visibility]
   * @param {string} [params.timeZone]
   * @param {string} [params.sendUpdates] - "all" | "externalOnly" | "none"
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async updateEvent(eventId, { calendarId = 'primary', summary, description, location, startTime, endTime, attendees, recurrence, visibility, timeZone, sendUpdates }) {
    try {
      if (!eventId) {
        return {
          success: false,
          error: { message: 'event_id is required' }
        };
      }

      const payload = {
        event_id: eventId,
        calendar_id: calendarId,
        summary,
        description,
        location,
        start_time: startTime,
        end_time: endTime,
        attendees,
        recurrence,
        visibility,
        time_zone: timeZone,
        send_updates: sendUpdates
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.put(`/calendar/events/${eventId}`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating Calendar event:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar evento del calendario
   * DELETE /calendar/events/{event_id}
   * 
   * @param {string} eventId - ID del evento (REQUIRED)
   * @param {Object} [options]
   * @param {string} [options.calendarId] - default: "primary"
   * @param {string} [options.sendUpdates] - "all" | "externalOnly" | "none"
   * @param {string} [options.deleteMode] - "single" | "all" (para eventos recurrentes)
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async deleteEvent(eventId, { calendarId = 'primary', sendUpdates, deleteMode } = {}) {
    try {
      if (!eventId) {
        return {
          success: false,
          error: { message: 'event_id is required' }
        };
      }

      const response = await connectionsClient.delete(`/calendar/events/${eventId}`, {
        data: {
          event_id: eventId,
          calendar_id: calendarId,
          send_updates: sendUpdates,
          delete_mode: deleteMode
        }
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error deleting Calendar event:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Verificar disponibilidad en el calendario
   * GET /calendar/availability
   * 
   * @param {Object} params
   * @param {string} params.timeMin - ISO datetime (REQUIRED)
   * @param {string} params.timeMax - ISO datetime (REQUIRED)
   * @param {string} [params.calendarIds] - IDs separados por coma
   * @param {boolean} [params.suggestTimes]
   * @param {number} [params.meetingDurationMinutes] - default: 60
   * @param {number} [params.maxSuggestions] - default: 5
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getAvailability({ timeMin, timeMax, calendarIds, suggestTimes, meetingDurationMinutes = 60, maxSuggestions = 5 }) {
    try {
      if (!timeMin || !timeMax) {
        return {
          success: false,
          error: { message: 'time_min and time_max are required' }
        };
      }

      const params = {
        time_min: timeMin,
        time_max: timeMax,
        calendar_ids: calendarIds,
        suggest_times: suggestTimes,
        meeting_duration_minutes: meetingDurationMinutes,
        max_suggestions: maxSuggestions
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get('/calendar/availability', { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error getting Calendar availability:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  // ============================================================================
  // 📊 GOOGLE SHEETS (/sheets) - 6 endpoints
  // ============================================================================

  /**
   * Crear nuevo spreadsheet
   * POST /sheets/spreadsheets
   * 
   * @param {Object} params
   * @param {string} params.title - Título del spreadsheet (REQUIRED)
   * @param {Array<Object>} [params.sheets] - Lista opcional de objetos Sheet según API de Google Sheets
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async createSpreadsheet({ title, sheets }) {
    try {
      if (!title) {
        return {
          success: false,
          error: { message: 'title is required' }
        };
      }

      const payload = {
        title,
        sheets
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/sheets/spreadsheets', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating Sheets spreadsheet:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener metadata del spreadsheet
   * GET /sheets/spreadsheets/{spreadsheet_id}
   * 
   * @param {string} spreadsheetId - ID del spreadsheet (REQUIRED)
   * @param {Object} [options]
   * @param {boolean} [options.includeGridData] - Si true, incluye datos de celdas (payload pesado)
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getSpreadsheet(spreadsheetId, { includeGridData = false } = {}) {
    try {
      if (!spreadsheetId) {
        return {
          success: false,
          error: { message: 'spreadsheet_id is required' }
        };
      }

      const params = {
        include_grid_data: includeGridData
      };

      const response = await connectionsClient.get(`/sheets/spreadsheets/${spreadsheetId}`, { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error getting Sheets spreadsheet:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Leer rango de celdas
   * GET /sheets/spreadsheets/{spreadsheet_id}/values
   * 
   * @param {string} spreadsheetId - ID del spreadsheet (REQUIRED)
   * @param {Object} params
   * @param {string} params.range - Rango en notación A1 (e.g., "Sheet1!A1:C10") (REQUIRED)
   * @param {string} [params.valueRenderOption] - FORMATTED_VALUE | UNFORMATTED_VALUE | FORMULA
   * @param {string} [params.dateTimeRenderOption] - SERIAL_NUMBER | FORMATTED_STRING
   * @param {string} [params.majorDimension] - ROWS | COLUMNS
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async readSpreadsheetRange(spreadsheetId, { range, valueRenderOption = 'FORMATTED_VALUE', dateTimeRenderOption = 'FORMATTED_STRING', majorDimension = 'ROWS' }) {
    try {
      if (!spreadsheetId || !range) {
        return {
          success: false,
          error: { message: 'spreadsheet_id and range are required' }
        };
      }

      const params = {
        range,
        value_render_option: valueRenderOption,
        date_time_render_option: dateTimeRenderOption,
        major_dimension: majorDimension
      };

      const response = await connectionsClient.get(`/sheets/spreadsheets/${spreadsheetId}/values`, { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error reading Sheets range:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Sobrescribir valores en un rango
   * PUT /sheets/spreadsheets/{spreadsheet_id}/values
   * 
   * @param {string} spreadsheetId - ID del spreadsheet (REQUIRED)
   * @param {Object} params
   * @param {string} params.rangeA1 - Rango en A1 (REQUIRED)
   * @param {Array<Array>} params.values - Matriz 2D de valores (REQUIRED)
   * @param {string} [params.valueInputOption] - RAW | USER_ENTERED
   * @param {boolean} [params.includeValuesInResponse] - Devolver valores actualizados
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async writeSpreadsheetRange(spreadsheetId, { rangeA1, values, valueInputOption = 'USER_ENTERED', includeValuesInResponse = false }) {
    try {
      if (!spreadsheetId || !rangeA1 || !values) {
        return {
          success: false,
          error: { message: 'spreadsheet_id, range_a1, and values are required' }
        };
      }

      const payload = {
        range_a1: rangeA1,
        values,
        value_input_option: valueInputOption,
        include_values_in_response: includeValuesInResponse
      };

      const response = await connectionsClient.put(`/sheets/spreadsheets/${spreadsheetId}/values`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error writing Sheets range:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Append filas al final de una tabla
   * POST /sheets/spreadsheets/{spreadsheet_id}/values/append
   * 
   * @param {string} spreadsheetId - ID del spreadsheet (REQUIRED)
   * @param {Object} params
   * @param {string} params.rangeA1 - Rango ancla (REQUIRED)
   * @param {Array<Array>} params.values - Matriz 2D de valores a anexar (REQUIRED)
   * @param {string} [params.valueInputOption] - RAW | USER_ENTERED
   * @param {string} [params.insertDataOption] - INSERT_ROWS | OVERWRITE
   * @param {boolean} [params.includeValuesInResponse] - Devolver valores anexados
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async appendSpreadsheetValues(spreadsheetId, { rangeA1, values, valueInputOption = 'USER_ENTERED', insertDataOption = 'INSERT_ROWS', includeValuesInResponse = false }) {
    try {
      if (!spreadsheetId || !rangeA1 || !values) {
        return {
          success: false,
          error: { message: 'spreadsheet_id, range_a1, and values are required' }
        };
      }

      const payload = {
        range_a1: rangeA1,
        values,
        value_input_option: valueInputOption,
        insert_data_option: insertDataOption,
        include_values_in_response: includeValuesInResponse
      };

      const response = await connectionsClient.post(`/sheets/spreadsheets/${spreadsheetId}/values/append`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error appending Sheets values:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Batch update - operaciones estructurales/de formato
   * POST /sheets/spreadsheets/{spreadsheet_id}/batch-update
   * 
   * @param {string} spreadsheetId - ID del spreadsheet (REQUIRED)
   * @param {Object} params
   * @param {Array<Object>} params.requests - Array de Request objects (addSheet, deleteSheet, etc.) (REQUIRED)
   * @param {boolean} [params.includeSpreadsheetInResponse] - Devolver spreadsheet completo
   * @param {Array<string>} [params.responseRanges] - Rangos A1 para limitar payload
   * @param {boolean} [params.responseIncludeGridData] - Incluir datos de celdas
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async batchUpdateSpreadsheet(spreadsheetId, { requests, includeSpreadsheetInResponse = false, responseRanges, responseIncludeGridData = false }) {
    try {
      if (!spreadsheetId || !requests || !Array.isArray(requests) || requests.length === 0) {
        return {
          success: false,
          error: { message: 'spreadsheet_id and requests array are required' }
        };
      }

      const payload = {
        requests,
        include_spreadsheet_in_response: includeSpreadsheetInResponse,
        response_ranges: responseRanges,
        response_include_grid_data: responseIncludeGridData
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post(`/sheets/spreadsheets/${spreadsheetId}/batch-update`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error batch updating Sheets:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  // 📧 GOOGLE GMAIL (/gmail) - 6 endpoints
  // ============================================================================

  /**
   * Enviar mensaje de Gmail
   * POST /gmail/messages/send
   * 
   * @param {Object} params
   * @param {Array<string>} params.to - Destinatarios (REQUIRED)
   * @param {string} params.subject - Asunto (REQUIRED)
   * @param {string} [params.bodyText] - Cuerpo en texto plano
   * @param {string} [params.bodyHtml] - Cuerpo en HTML
   * @param {Array<string>} [params.cc] - CC
   * @param {Array<string>} [params.bcc] - BCC
   * @param {string} [params.replyTo] - Reply-To
   * @param {string} [params.threadId] - Thread ID para responder
   * @param {Array<Object>} [params.attachments] - Adjuntos
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async sendGmailMessage({ to, subject, bodyText, bodyHtml, cc, bcc, replyTo, threadId, attachments }) {
    try {
      if (!to || !Array.isArray(to) || to.length === 0) {
        return {
          success: false,
          error: { message: 'to (array of recipients) is required' }
        };
      }
      if (!subject) {
        return {
          success: false,
          error: { message: 'subject is required' }
        };
      }

      const payload = {
        to,
        subject,
        body_text: bodyText,
        body_html: bodyHtml,
        cc,
        bcc,
        reply_to: replyTo,
        thread_id: threadId,
        attachments
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/gmail/messages/send', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error sending Gmail message:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar mensajes de Gmail
   * GET /gmail/messages
   * 
   * @param {Object} [options]
   * @param {string} [options.query] - Search query estilo Gmail
   * @param {Array<string>} [options.labelIds] - IDs de labels
   * @param {number} [options.maxResults] - Máximo por página (1-500, default 10)
   * @param {string} [options.pageToken] - Token de paginación
   * @param {boolean} [options.includeSpamTrash] - Incluir spam/trash
   * @param {string} [options.formatType] - Formato: minimal|full|metadata|raw
   * @param {string} [options.sortOrder] - asc|desc
   * @param {string} [options.startDate] - Fecha inicio
   * @param {string} [options.endDate] - Fecha fin
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async listGmailMessages({ query, labelIds, maxResults = 10, pageToken, includeSpamTrash = false, formatType = 'minimal', sortOrder = 'desc', startDate, endDate } = {}) {
    try {
      const params = {
        query,
        label_ids: labelIds ? labelIds.join(',') : undefined,
        max_results: maxResults,
        page_token: pageToken,
        include_spam_trash: includeSpamTrash,
        format_type: formatType,
        sort_order: sortOrder,
        start_date: startDate,
        end_date: endDate
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get('/gmail/messages', { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error listing Gmail messages:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener mensaje específico de Gmail
   * GET /gmail/messages/{message_id}
   * 
   * @param {string} messageId - ID del mensaje (REQUIRED)
   * @param {Object} [options]
   * @param {string} [options.formatType] - Formato: minimal|full|metadata|raw
   * @param {Array<string>} [options.metadataHeaders] - Headers a incluir
   * @param {boolean} [options.includeAttachments] - Incluir adjuntos
   * @param {boolean} [options.downloadAttachments] - Descargar adjuntos a S3
   * @param {boolean} [options.processHtml] - Procesar HTML
   * @param {boolean} [options.extractLinks] - Extraer links
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getGmailMessage(messageId, { formatType = 'full', metadataHeaders, includeAttachments = true, downloadAttachments = false, processHtml = true, extractLinks = false } = {}) {
    try {
      if (!messageId) {
        return {
          success: false,
          error: { message: 'message_id is required' }
        };
      }

      const params = {
        format_type: formatType,
        metadata_headers: metadataHeaders ? metadataHeaders.join(',') : undefined,
        include_attachments: includeAttachments,
        download_attachments: downloadAttachments,
        process_html: processHtml,
        extract_links: extractLinks
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get(`/gmail/messages/${messageId}`, { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error getting Gmail message:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Modificar labels de mensajes (mark read/unread)
   * POST /gmail/messages/modify
   * 
   * @param {Object} params
   * @param {Array<string>} params.messageIds - IDs de mensajes (REQUIRED)
   * @param {boolean} [params.markAsRead] - true = leído, false = no leído
   * @param {boolean} [params.verifyStatus] - Verificar éxito
   * @param {number} [params.batchSize] - Tamaño de lote
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async modifyGmailMessages({ messageIds, markAsRead = true, verifyStatus = true, batchSize = 100 }) {
    try {
      if (!messageIds || !Array.isArray(messageIds) || messageIds.length === 0) {
        return {
          success: false,
          error: { message: 'message_ids array is required' }
        };
      }

      const payload = {
        message_ids: messageIds,
        mark_as_read: markAsRead,
        verify_status: verifyStatus,
        batch_size: batchSize
      };

      const response = await connectionsClient.post('/gmail/messages/modify', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error modifying Gmail messages:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Sincronizar mensajes de Gmail
   * POST /gmail/messages/sync
   * 
   * @param {Object} [options]
   * @param {string} [options.syncType] - full|incremental|partial
   * @param {string} [options.lastHistoryId] - Último history ID
   * @param {string} [options.lastSyncTime] - Timestamp último sync
   * @param {number} [options.maxResults] - Máximo por lote
   * @param {Array<string>} [options.labelIds] - Labels específicos
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async syncGmailMessages({ syncType = 'incremental', lastHistoryId, lastSyncTime, maxResults = 100, labelIds } = {}) {
    try {
      const payload = {
        sync_type: syncType,
        last_history_id: lastHistoryId,
        last_sync_time: lastSyncTime,
        max_results: maxResults,
        label_ids: labelIds
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/gmail/messages/sync', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error syncing Gmail messages:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Leer emails (alias de list)
   * GET /gmail/emails
   * 
   * @param {Object} [options] - Mismos parámetros que listGmailMessages
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async readGmailEmails(options = {}) {
    try {
      const params = {
        query: options.query,
        label_ids: options.labelIds ? options.labelIds.join(',') : undefined,
        max_results: options.maxResults || 10,
        page_token: options.pageToken,
        include_spam_trash: options.includeSpamTrash || false,
        format_type: options.formatType || 'minimal',
        sort_order: options.sortOrder || 'desc',
        start_date: options.startDate,
        end_date: options.endDate
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get('/gmail/emails', { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error reading Gmail emails:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  // 📁 GOOGLE DRIVE (/drive) - 8 endpoints
  // ============================================================================

  /**
   * Crear archivo en Drive
   * POST /drive/files
   * 
   * @param {Object} params
   * @param {string} params.name - Nombre del archivo (REQUIRED)
   * @param {Array<string>} [params.parents] - IDs de carpetas padre
   * @param {string} [params.mimeType] - MIME type
   * @param {string} [params.description] - Descripción
   * @param {boolean} [params.starred] - Marcar con estrella
   * @param {Object} [params.properties] - Propiedades custom
   * @param {Object} [params.appProperties] - App properties
   * @param {string} [params.content] - Contenido (para archivos de texto)
   * @param {string} [params.uploadType] - Tipo de upload
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async createDriveFile({ name, parents, mimeType, description, starred = false, properties, appProperties, content, uploadType = 'multipart' }) {
    try {
      if (!name) {
        return {
          success: false,
          error: { message: 'name is required' }
        };
      }

      const payload = {
        name,
        parents,
        mime_type: mimeType,
        description,
        starred,
        properties,
        app_properties: appProperties,
        content,
        upload_type: uploadType
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/drive/files', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar archivos de Drive
   * GET /drive/files
   * 
   * @param {Object} [options]
   * @param {number} [options.maxResults] - Máximo de archivos (1-1000, default 20)
   * @param {string} [options.pageToken] - Token de paginación
   * @param {string} [options.query] - Search query estilo Drive
   * @param {string} [options.folderId] - ID de la carpeta a listar
   * @param {Array<string>} [options.fileTypes] - MIME types a filtrar
   * @param {boolean} [options.includeShared] - Incluir archivos compartidos
   * @param {boolean} [options.includeTrashed] - Incluir archivos en papelera
   * @param {string} [options.orderBy] - Orden de la lista
   * @param {string} [options.fields] - Campos específicos a devolver
   * @param {boolean} [options.supportsAllDrives] - Soporte de shared drives
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async listDriveFiles({ maxResults = 20, pageToken, query, folderId, fileTypes, includeShared = true, includeTrashed = false, orderBy = 'modifiedTime desc', fields, supportsAllDrives = false } = {}) {
    try {
      const params = {
        max_results: maxResults,
        page_token: pageToken,
        query,
        folder_id: folderId,
        file_types: fileTypes ? fileTypes.join(',') : undefined,
        include_shared: includeShared,
        include_trashed: includeTrashed,
        order_by: orderBy,
        fields,
        supports_all_drives: supportsAllDrives
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get('/drive/files', { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error listing Drive files:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Actualizar archivo de Drive
   * PUT /drive/files/{file_id}
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} updates - Campos a actualizar
   * @param {string} [updates.name] - Nuevo nombre
   * @param {string} [updates.description] - Nueva descripción
   * @param {boolean} [updates.starred] - Estado de estrellado
   * @param {boolean} [updates.trashed] - Mover a/sacar de papelera
   * @param {Array<string>} [updates.parents] - Nuevos parents
   * @param {Object} [updates.properties] - Propiedades custom
   * @param {Object} [updates.appProperties] - App properties
   * @param {string} [updates.content] - Nuevo contenido
   * @param {boolean} [updates.supportsAllDrives] - Soporte de shared drives
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async updateDriveFile(fileId, { name, description, starred, trashed, parents, properties, appProperties, content, supportsAllDrives = false } = {}) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const payload = {
        name,
        description,
        starred,
        trashed,
        parents,
        properties,
        app_properties: appProperties,
        content,
        supports_all_drives: supportsAllDrives
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.put(`/drive/files/${fileId}`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar archivo de Drive
   * DELETE /drive/files/{file_id}
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} [options]
   * @param {boolean} [options.permanentDelete] - Eliminar permanentemente
   * @param {boolean} [options.verifyDeletion] - Verificar eliminación
   * @param {boolean} [options.backupMetadata] - Backup de metadata
   * @param {boolean} [options.supportsAllDrives] - Soporte de shared drives
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async deleteDriveFile(fileId, { permanentDelete = false, verifyDeletion = true, backupMetadata = true, supportsAllDrives = false } = {}) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const params = {
        permanent_delete: permanentDelete,
        verify_deletion: verifyDeletion,
        backup_metadata: backupMetadata,
        supports_all_drives: supportsAllDrives
      };

      const response = await connectionsClient.delete(`/drive/files/${fileId}`, { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error deleting Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Mover archivo de Drive
   * POST /drive/files/{file_id}/move
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} [options]
   * @param {string} [options.destinationFolderId] - ID de la carpeta destino
   * @param {Array<string>} [options.removeFromParents] - Parents de los que sacar
   * @param {string} [options.newName] - Renombrar durante el move
   * @param {boolean} [options.verifyMove] - Verificar que el move se completó
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async moveDriveFile(fileId, { destinationFolderId, removeFromParents, newName, verifyMove = true } = {}) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const payload = {
        destination_folder_id: destinationFolderId,
        remove_from_parents: removeFromParents,
        new_name: newName,
        verify_move: verifyMove
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post(`/drive/files/${fileId}/move`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error moving Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Descargar archivo de Drive
   * GET /drive/files/{file_id}/download
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} [options]
   * @param {string} [options.exportFormat] - MIME de exportación para Google Workspace files
   * @param {string} [options.savePath] - Path local donde persistir
   * @param {boolean} [options.includeMetadata] - Incluir metadata
   * @param {boolean} [options.verifyChecksum] - Verificar checksum
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async downloadDriveFile(fileId, { exportFormat, savePath, includeMetadata = true, verifyChecksum = false } = {}) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const params = {
        export_format: exportFormat,
        save_path: savePath,
        include_metadata: includeMetadata,
        verify_checksum: verifyChecksum
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get(`/drive/files/${fileId}/download`, { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error downloading Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Compartir archivo de Drive
   * POST /drive/files/{file_id}/share
   * 
   * @param {string} fileId - ID del archivo (REQUIRED)
   * @param {Object} params
   * @param {Array<Object>} [params.permissions] - Lista de permission payloads (modo bulk)
   * @param {string} [params.role] - Role para sharing single-recipient
   * @param {string} [params.type] - Type para single-recipient
   * @param {string} [params.emailAddress] - Email para user/group
   * @param {string} [params.domain] - Dominio para sharing tipo domain
   * @param {boolean} [params.allowFileDiscovery] - Permitir descubrimiento
   * @param {boolean} [params.sendNotificationEmail] - Enviar email notificación
   * @param {string} [params.emailMessage] - Mensaje custom del email
   * @param {boolean} [params.transferOwnership] - Transferir ownership
   * @param {boolean} [params.makePublic] - Hacer público
   * @param {boolean} [params.supportsAllDrives] - Soporte de shared drives
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async shareDriveFile(fileId, { permissions, role, type, emailAddress, domain, allowFileDiscovery = false, sendNotificationEmail = true, emailMessage, transferOwnership = false, makePublic = false, supportsAllDrives = false } = {}) {
    try {
      if (!fileId) {
        return {
          success: false,
          error: { message: 'file_id is required' }
        };
      }

      const payload = {
        permissions,
        role,
        type,
        email_address: emailAddress,
        domain,
        allow_file_discovery: allowFileDiscovery,
        send_notification_email: sendNotificationEmail,
        email_message: emailMessage,
        transfer_ownership: transferOwnership,
        make_public: makePublic,
        supports_all_drives: supportsAllDrives
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post(`/drive/files/${fileId}/share`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error sharing Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Subir archivo binario a Drive
   * POST /drive/files/upload
   * 
   * @param {Object} params
   * @param {string} params.name - Nombre del archivo (REQUIRED)
   * @param {string|Blob} params.fileContent - Contenido binario o base64 (REQUIRED)
   * @param {string} [params.description] - Descripción
   * @param {Array<string>} [params.parents] - IDs de carpetas padre
   * @param {string} [params.mimeType] - MIME type
   * @param {boolean} [params.resumable] - Usar flujo resumable upload
   * @param {Object} [params.properties] - Propiedades custom
   * @param {Object} [params.appProperties] - App properties
   * @param {boolean} [params.makePublic] - Hacer público
   * @param {Array<string>} [params.shareWithEmails] - Emails a los que compartir
   * @param {Array<Object>} [params.permissions] - Permission payloads
   * @param {boolean} [params.convertToGoogleFormat] - Convertir a formato Google Workspace
   * @param {boolean} [params.autoRename] - Resolver conflictos de nombre
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async uploadDriveFile({ name, fileContent, description, parents, mimeType, resumable = false, properties, appProperties, makePublic = false, shareWithEmails, permissions, convertToGoogleFormat = false, autoRename = false }) {
    try {
      if (!name) {
        return {
          success: false,
          error: { message: 'name is required' }
        };
      }
      if (!fileContent) {
        return {
          success: false,
          error: { message: 'file_content is required' }
        };
      }

      const payload = {
        name,
        file_content: fileContent,
        description,
        parents,
        mime_type: mimeType,
        resumable,
        properties,
        app_properties: appProperties,
        make_public: makePublic,
        share_with_emails: shareWithEmails,
        permissions,
        convert_to_google_format: convertToGoogleFormat,
        auto_rename: autoRename
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post('/drive/files/upload', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error uploading Drive file:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  // ==================== GOOGLE DOCS ====================

  /**
   * Crear documento de Google Docs
   * POST /docs/documents
   * 
   * @param {Object} params
   * @param {string} params.title - Título del documento (REQUIRED)
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async createDocsDocument({ title }) {
    try {
      if (!title || title.trim().length === 0) {
        return {
          success: false,
          error: { message: 'title is required and cannot be empty' }
        };
      }

      const payload = {
        title
      };

      const response = await connectionsClient.post('/docs/documents', payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating Docs document:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener contenido de documento de Google Docs
   * GET /docs/documents/{document_id}
   * 
   * @param {string} documentId - ID del documento (REQUIRED)
   * @param {Object} params
   * @param {string} [params.suggestionsViewMode='DEFAULT_FOR_CURRENT_ACCESS'] - Modo de vista de sugerencias
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getDocsDocument(documentId, { suggestionsViewMode = 'DEFAULT_FOR_CURRENT_ACCESS' } = {}) {
    try {
      if (!documentId) {
        return {
          success: false,
          error: { message: 'document_id is required' }
        };
      }

      const params = {
        suggestions_view_mode: suggestionsViewMode
      };

      // Limpiar campos undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await connectionsClient.get(`/docs/documents/${documentId}`, { params });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error getting Docs document:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Aplicar batch update a documento de Google Docs
   * POST /docs/documents/{document_id}/batch-update
   * 
   * @param {string} documentId - ID del documento (REQUIRED)
   * @param {Object} params
   * @param {Array<Object>} params.requests - Lista de Request objects (insertText, replaceAllText, etc.) (REQUIRED)
   * @param {Object} [params.writeControl] - WriteControl object para concurrencia
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async batchUpdateDocsDocument(documentId, { requests, writeControl } = {}) {
    try {
      if (!documentId) {
        return {
          success: false,
          error: { message: 'document_id is required' }
        };
      }
      if (!requests || !Array.isArray(requests) || requests.length === 0) {
        return {
          success: false,
          error: { message: 'requests is required and must be a non-empty array' }
        };
      }

      const payload = {
        requests,
        write_control: writeControl
      };

      // Limpiar campos undefined
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await connectionsClient.post(`/docs/documents/${documentId}/batch-update`, payload);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error batch updating Docs document:', error);
      return { success: false, error: handleApiError(error) };
    }
  }
}

export default new GoogleService();
